/**
 * What the host of a multiplayer room decides for everyone in it. Each
 * player's own options decide these in solo play.
 */

export const AUTO_CROSS_LEVELS = ['off', 'line_complete', 'line_and_separators'] as const;
/** Off; crossing the rest of a finished line; also the cells before, between, and after finished clues. */
export type AutoCrossLevel = (typeof AUTO_CROSS_LEVELS)[number];

export interface RoomSettings {
  /** Cross out cells finished clues rule out, for everyone's moves. */
  autoCross: AutoCrossLevel;
  /** The Hint button. */
  hints: boolean;
  /** Check puzzle (fixes mistakes on the board). */
  checks: boolean;
}

export const DEFAULT_ROOM_SETTINGS: RoomSettings = { autoCross: 'off', hints: true, checks: true };

export function isAutoCrossLevel(value: unknown): value is AutoCrossLevel {
  return typeof value === 'string' && (AUTO_CROSS_LEVELS as readonly string[]).includes(value);
}

/** Whether two settings say the same. */
export function sameRoomSettings(a: RoomSettings, b: RoomSettings): boolean {
  return (Object.keys(a) as Array<keyof RoomSettings>).every((key) => a[key] === b[key]);
}

/** `value`'s valid settings over `base`; anything unknown or malformed is ignored. */
export function mergeRoomSettings(base: RoomSettings, value: unknown): RoomSettings {
  if (typeof value !== 'object' || value === null) return base;
  const changes = value as Record<string, unknown>;
  return {
    autoCross: isAutoCrossLevel(changes.autoCross) ? changes.autoCross : base.autoCross,
    hints: typeof changes.hints === 'boolean' ? changes.hints : base.hints,
    checks: typeof changes.checks === 'boolean' ? changes.checks : base.checks,
  };
}
